/**
 * WebSocket client for real-time updates (Phase C)
 *
 * Provides instant push notifications and messages instead of polling.
 */

import { io, Socket } from 'socket.io-client';
import { create } from 'zustand';
import { useAuthStore } from '@/features/auth/store';
import { signOutWithNotice } from './signOutNotice';
import { WS_URL } from './wsUrl';

// The socket target is resolved once, in lib/wsUrl.ts, and shared with the platform client.

interface WebSocketState {
  socket: Socket | null;
  connected: boolean;
  connect: () => void;
  disconnect: () => void;
}

export const useWebSocketStore = create<WebSocketState>()((set, get) => ({
  socket: null,
  connected: false,

  connect: () => {
    const existing = get().socket;
    if (existing?.connected) return;

    const token = useAuthStore.getState().accessToken;
    if (!token) {
      console.warn('WebSocket: No access token available');
      return;
    }

    const socket = io(WS_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      // Never stop trying. This was 10, which with the 1s→5s backoff meant a tab gave up
      // PERMANENTLY after roughly 40 seconds — shorter than a rolling deploy or an API
      // restart. Once it gave up nothing re-armed it: the tab stayed silently stale until
      // the user happened to reload. There is no cost to retrying forever (the backoff is
      // capped at 5s and the socket is torn down on logout), and the reconnect handler in
      // WebSocketProvider re-fetches everything that changed while it was down.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => {
      console.log('WebSocket connected');
      set({ connected: true });
    });

    socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      set({ connected: false });
    });

    socket.on('connect_error', (err) => {
      console.warn('WebSocket connection error:', err.message);
      set({ connected: false });
    });

    // The server tells THIS signed-in socket why its session is ending (disconnectUser). When the
    // account was deactivated, sign the tab out and explain it on the login page (CLAUDE.md #25).
    socket.on('session:revoked', (payload: { reason?: string } | undefined) => {
      if (payload?.reason === 'account_deactivated') {
        signOutWithNotice('account_deactivated', () => useAuthStore.getState().clear());
      }
    });

    set({ socket });
  },

  disconnect: () => {
    const { socket } = get();
    if (socket) {
      socket.disconnect();
      set({ socket: null, connected: false });
    }
  },
}));

// ─── Event subscription helpers ───────────────────────────────

type NotificationEvent = {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  createdAt: string;
};

type MessageEvent = {
  id: string;
  threadId: string;
  senderId: string;
  senderName: string;
  sequence: number;
  createdAt: string;
  // body is intentionally absent — fetch message content via HTTP on receipt
};

type TypingEvent = {
  userId: string;
  threadId: string;
};

type AllocationUpdateEvent = {
  allocationId: string;
  caseId: string;
  status: string;
};

type CalendarUpdateEvent = {
  eventId: string;
  action: 'created' | 'updated' | 'deleted';
};

type EventCallback<T> = (data: T) => void;

/**
 * Subscribe to new notification events
 */
export function onNotification(callback: EventCallback<NotificationEvent>): () => void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return () => {};

  socket.on('notification:new', callback);
  return () => socket.off('notification:new', callback);
}

/**
 * Subscribe to new message events
 */
export function onMessage(callback: EventCallback<MessageEvent>): () => void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return () => {};

  socket.on('message:new', callback);
  return () => socket.off('message:new', callback);
}

/**
 * Subscribe to typing start events
 */
export function onTypingStart(callback: EventCallback<TypingEvent>): () => void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return () => {};

  socket.on('typing:start', callback);
  return () => socket.off('typing:start', callback);
}

/**
 * Subscribe to typing stop events
 */
export function onTypingStop(callback: EventCallback<TypingEvent>): () => void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return () => {};

  socket.on('typing:stop', callback);
  return () => socket.off('typing:stop', callback);
}

/**
 * Subscribe to allocation state-change events
 */
export function onAllocationUpdate(callback: EventCallback<AllocationUpdateEvent>): () => void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return () => {};
  socket.on('allocation:updated', callback);
  return () => socket.off('allocation:updated', callback);
}

/**
 * Subscribe to inbox sync events — fires when the Gmail sync worker
 * finds new emails for this user. Triggers an immediate refetch so
 * the inbox updates within seconds of an email arriving.
 */
export function onInboxUpdate(callback: EventCallback<{ mailboxId: string; newCount: number }>): () => void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return () => {};
  socket.on('inbox:updated', callback);
  return () => socket.off('inbox:updated', callback);
}

/**
 * Subscribe to matters list change events (matter created/assigned/updated)
 */
export function onMattersUpdate(
  callback: EventCallback<{ matterId: string; source?: string; event?: string }>,
): () => void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return () => {};
  socket.on('matters:updated', callback);
  return () => socket.off('matters:updated', callback);
}

/**
 * Subscribe to calendar create/update/delete events
 */
export function onCalendarUpdate(callback: EventCallback<CalendarUpdateEvent>): () => void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return () => {};
  socket.on('calendar:updated', callback);
  return () => socket.off('calendar:updated', callback);
}

type AccessChangedEvent = {
  scope: 'capabilities' | 'matter_override';
  matterId: string | null;
};

/**
 * A support ticket this user is a party to has changed — Support replied, or their own reply
 * landed from another tab.
 *
 * Identifier only, by design (see SUPPORT_TICKET_UPDATED in api/src/lib/websocket.ts). The
 * ticket body, the author and Support's internal notes never travel over the socket; the client
 * refetches the ticket through `GET /support/:id`, which is requester-scoped and already
 * excludes internal notes. An internal note therefore produces no event here at all.
 */
export type SupportUpdateEvent = {
  ticketId: string;
};

/**
 * Subscribe to support ticket updates for the signed-in member.
 *
 * Note what this deliberately does NOT do: it carries no notion of "read". The Support badge
 * counts UNANSWERED replies and is derived on the server (conversationSignals in
 * support.service.ts); receiving this event refreshes that derivation, it never clears it.
 */
export function onSupportUpdate(callback: EventCallback<SupportUpdateEvent>): () => void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return () => {};
  socket.on('support:ticket-updated', callback);
  return () => socket.off('support:ticket-updated', callback);
}

/**
 * Subscribe to clerk access-change events — fires for the CLERK when the
 * barrister changes their capabilities or a per-matter exception. The client
 * re-scopes (refetches) immediately so the clerk's pages, navigation and search
 * reflect the new access without a refresh or re-login.
 */
export function onAccessChanged(callback: EventCallback<AccessChangedEvent>): () => void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return () => {};
  socket.on('clerk:access_changed', callback);
  return () => socket.off('clerk:access_changed', callback);
}

/**
 * Join a message thread room (to receive messages for that thread)
 */
export function joinThread(threadId: string): void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return;
  socket.emit('thread:join', { threadId });
}

/**
 * Leave a message thread room
 */
export function leaveThread(threadId: string): void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return;
  socket.emit('thread:leave', { threadId });
}

/**
 * Emit typing start event
 */
export function emitTypingStart(threadId: string): void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return;
  socket.emit('typing:start', { threadId });
}

/**
 * Emit typing stop event
 */
export function emitTypingStop(threadId: string): void {
  const { socket } = useWebSocketStore.getState();
  if (!socket) return;
  socket.emit('typing:stop', { threadId });
}
